import { useQueryClient } from '@tanstack/react-query'
import { HouseIcon, TagIcon } from 'lucide-react'
import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { queryKeys, useRunTool, useSettings, useTags } from '@/lib/items'
import { tagLabel, togglePersonal } from '@/lib/tags'

/**
 * Settings → Tags: the tags in use, and which are personal. A personal tag keeps its items out
 * of the working hours hugo plans: they keep their dates and their place on the board, but never
 * push a work promise (decision 97).
 */
export function TagsCard() {
  const tags = useTags()
  const settings = useSettings()
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const [saving, setSaving] = useState<string | null>(null)
  const personal = settings.data?.personalTags ?? []

  const toggle = async (tag: string, on: boolean) => {
    setSaving(tag)
    const result = await run(
      'settings.update',
      { personalTags: togglePersonal(personal, tag, on) },
      {
        done: on ? `${tagLabel(tag)} is personal` : `${tagLabel(tag)} counts as work`,
        failed: 'Could not change the tag',
      },
    )
    if (result) queryClient.setQueryData(queryKeys.settings, result)
    setSaving(null)
  }

  const list = tags.data ?? []
  return (
    <Card data-testid="tags-settings">
      <CardHeader>
        <CardTitle>Tags</CardTitle>
        <CardDescription>
          Group items by area with #tags, e.g. “Book the dentist #family”. Mark a tag personal and
          hugo keeps its items out of your working hours: they keep their dates, but never push your
          work.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="no-tags"
          >
            <TagIcon aria-hidden="true" className="size-4" />
            No tags yet. Add one with # when you capture, or in an item's details.
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {list.map((tag) => {
              const id = `tag-personal-${tag.name}`
              return (
                <li
                  key={tag.name}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  data-testid="tag-row"
                  data-tag={tag.name}
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-sm font-medium">{tagLabel(tag.name)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {tag.open === 1 ? '1 item' : `${tag.open} items`}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Label htmlFor={id} className="flex items-center gap-1 text-sm font-normal">
                      <HouseIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                      Personal
                    </Label>
                    <Switch
                      id={id}
                      aria-label={`${tagLabel(tag.name)} is personal`}
                      checked={tag.personal}
                      disabled={saving !== null || !settings.data}
                      onCheckedChange={(checked) => void toggle(tag.name, checked)}
                    />
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
